import { useEffect, useState, type FC } from 'react';
import { Button, Toggle, InlineNotification } from '@carbon/react';
import { Close, Reset } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import routes from '~/routes';
import { LaunchLink } from 'components/launch-link';
import { formatMutationResource } from 'utils/misc';
import type {
  PreDrillTargetZoneIntervalRow,
  IntersectedGridsResponse,
} from 'types/api-responses';
import { preDrillTargetZoneIntervalsAtom } from '../atoms';
import { IntervalTable, intervalRowKey } from './interval-table';
import {
  intersectedGridReasonText,
  permittedDepthOutsideSection,
} from '../utils';
import styles from './change-match-drawer.module.scss';

interface ChangeMatchDrawerProps {
  /** Null at the bore address before the bore's geometry workspace exists. */
  workspaceId: number | null;
  /** Inside a Well Workspace the catalog is keyed on the bore instead of the geometry
   *  workspace; both resolve the same row. */
  bore?: { wellWorkspaceId: number; uwi14: string };
  /** Overrides the workspace's default collection for the catalog scope. */
  structureGridCollectionId: number | null;
  /** Confidence column state (see IntervalTable). */
  aiRunHappened: boolean;
  /** Confirm in flight — disables Save. */
  saving: boolean;
  /** Story 8: grid depth ranges + permitted-depth pill; null when not served. */
  intersectedGrids?: IntersectedGridsResponse | null;
  onClose: () => void;
  /** Commit the pick — parent confirms it with manualOverride: true. */
  onSave: (row: PreDrillTargetZoneIntervalRow) => void;
}

// Fetch the whole catalog for the scope in one page; the DataTable handles
// search / sort / pagination client-side (the per-basin catalog is small).
const CATALOG_PAGE_SIZE = 500;

// Change Match drawer (right panel): the workspace-scoped geologic-interval
// catalog as a single-select DataTable. Save confirms the pick as manual.
export const ChangeMatchDrawer: FC<ChangeMatchDrawerProps> = ({
  workspaceId,
  bore,
  structureGridCollectionId,
  aiRunHappened,
  saving,
  intersectedGrids = null,
  onClose,
  onSave,
}) => {
  const [resource, fetchIntervals] = useAtom(preDrillTargetZoneIntervalsAtom);
  const [showAll, setShowAll] = useState(false);
  // Null while an interval carries the depth or the indicator does not apply.
  const outsideSection = permittedDepthOutsideSection(intersectedGrids);
  const [selected, setSelected] =
    useState<PreDrillTargetZoneIntervalRow | null>(null);

  // `show_all` toggles the collection scope server-side; everything else the
  // DataTable filters/sorts/paginates on the client. In show-all the server
  // returns the whole expanded set (page params ignored).
  useEffect(() => {
    fetchIntervals({
      type: 'POST',
      url: '/queries/pre_drill_target_zone_intervals',
      data: {
        query: {
          ...(bore
            ? { well_workspace_id: bore.wellWorkspaceId, uwi14: bore.uwi14 }
            : { wellbore_geometry_workspace_id: workspaceId ?? undefined }),
          show_all: showAll,
          structure_grid_collection_id: structureGridCollectionId ?? undefined,
          page: 1,
          page_size: CATALOG_PAGE_SIZE,
        },
      },
    });
  }, [workspaceId, bore, structureGridCollectionId, showAll, fetchIntervals]);

  const { loading, data, error } = formatMutationResource(resource);
  const rows = data?.results ?? [];

  // No basin resolves for this permit (no covering collection, no IHS basin):
  // there is no collection-scoped view at all, so show-all is forced on and the
  // toggle locked. The effect flips the state (triggering the show-all fetch)
  // the first time a response reports the unresolved basin.
  const basinResolved = data?.basinResolved ?? true;
  useEffect(() => {
    if (data && !data.basinResolved && !showAll) setShowAll(true);
  }, [data, showAll]);

  const nonDefaultWarning = selected && !selected.isDefaultCollection;
  const noGeologicReferenceWarning =
    selected && !selected.locationIntersectGrid;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Change Match</h2>
          <p className={styles.subtitle}>
            Re-resolve the pre-drill target zone against the geologic-interval
            catalog.
          </p>
        </div>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Close}
          iconDescription="Close"
          onClick={onClose}
        />
      </div>

      <div className={styles.body}>
        <p className={styles.heading}>Resolve Target Zone Match</p>

        <div className={styles.pathRow}>
          {/* Selecting from the catalog is the only in-drawer path, so it reads
              as plain text rather than a one-option radio. */}
          <span>Select from Geologic Intervals</span>
          {/* Creating a new geologic interval happens in Formation Management —
              link out (new tab) instead of an in-drawer create flow. */}
          <LaunchLink
            to={routes.formationManagementPath}
            className={styles.createLink}
            label="Create New Geologic Interval in a new tab"
          >
            Create New Geologic Interval
          </LaunchLink>
        </div>

        <div className={styles.showAll}>
          <Toggle
            id="change-match-show-all"
            size="sm"
            labelText=""
            labelA=""
            labelB=""
            toggled={showAll}
            disabled={!basinResolved}
            onToggle={(next) => {
              setSelected(null);
              setShowAll(next);
            }}
          />
          <label
            htmlFor="change-match-show-all"
            className={styles.showAllLabel}
          >
            Show all geologic intervals
          </label>
        </div>
        <p className={styles.help}>
          {!basinResolved
            ? 'This permit does not intersect a basin. Showing all geologic intervals; any can be assigned as the target zone.'
            : showAll
              ? "Showing every structure grid and geologic interval in this permit's basin, grouped by collection. Any interval can be assigned as the target zone."
              : 'Showing geologic intervals in the selected structure grid collection. Toggle on to browse the whole basin.'}
        </p>

        {nonDefaultWarning && (
          <InlineNotification
            className={styles.notice}
            kind="warning"
            lowContrast
            hideCloseButton
            title="Non-default structure grid collection"
            subtitle="The chosen interval is not in this basin's default structure grid collection; results may not be compatible with other well geometries."
          />
        )}
        {noGeologicReferenceWarning && (
          <InlineNotification
            className={styles.notice}
            kind="warning"
            lowContrast
            hideCloseButton
            title="No geologic reference"
            subtitle="The chosen zone has no geologic reference at this location. Its wellbore geometry will be generalized via the permitted depth or a user-entered value."
          />
        )}
        {!loading && error?.type && (
          <InlineNotification
            className={styles.notice}
            kind="error"
            lowContrast
            hideCloseButton
            title="Failed to load geologic intervals"
          />
        )}

        <div className={styles.tableActions}>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Reset}
            disabled={selected === null}
            onClick={() => setSelected(null)}
          >
            Reset selection
          </Button>
        </div>

        {intersectedGrids && !intersectedGrids.applicable && (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            className={styles.notice}
            title="No permitted depth indicator for this wellbore."
            subtitle={
              intersectedGridReasonText(intersectedGrids.reason) ??
              'The permitted depth could not be placed against these grids.'
            }
          />
        )}

        {/* The indicator applies but no interval claims the depth. Saying so beats an
            unexplained absence, which reads the same as the indicator being off. */}
        {outsideSection && (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            className={styles.notice}
            title="No interval contains the permitted depth."
            subtitle={outsideSection}
          />
        )}

        <IntervalTable
          rows={rows}
          selectedKey={selected ? intervalRowKey(selected) : null}
          aiRunHappened={aiRunHappened}
          isLoading={loading && !data}
          intersectedGrids={intersectedGrids}
          onSelect={setSelected}
        />
      </div>

      <div className={styles.footer}>
        <Button kind="secondary" size="lg" onClick={onClose}>
          Cancel
        </Button>
        <Button
          kind="primary"
          size="lg"
          disabled={selected === null || saving}
          onClick={() => {
            if (selected) onSave(selected);
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};
